import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Pagination, StatCard } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

const FALLBACK_CATEGORIES = [
  { slug: 'encyklopedia-samoloty', label: 'Samoloty' },
  { slug: 'encyklopedia-smiglowce', label: 'Śmigłowce' },
  { slug: 'encyklopedia-drony', label: 'Drony' },
  { slug: 'encyklopedia-radary', label: 'Radary' },
  { slug: 'encyklopedia-obrona-powietrzna', label: 'Obrona powietrzna' },
  { slug: 'encyklopedia-rakiety', label: 'Rakiety' },
  { slug: 'encyklopedia-kosmos', label: 'Kosmos' },
  { slug: 'encyklopedia-balony', label: 'Balony' },
  { slug: 'encyklopedia-meteorologia', label: 'Meteorologia' },
  { slug: 'encyklopedia-bezpieczenstwo', label: 'Bezpieczeństwo' },
];

function fmt(value: unknown): string {
  if (!value) return '—';
  const time = Date.parse(String(value));
  if (!Number.isFinite(time)) return String(value).slice(0, 40);
  return new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(time));
}

function localInput(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function statusTone(status: string): 'neutral' | 'info' | 'warning' | 'success' | 'danger' {
  if (['PUBLISHED', 'PUSHED'].includes(status)) return 'success';
  if (['REVIEW'].includes(status)) return 'warning';
  if (['FAILED'].includes(status)) return 'danger';
  if (['DRAFT'].includes(status)) return 'info';
  return 'neutral';
}

function parseArray(text: string, field: string): Any[] {
  const parsed = JSON.parse(text || '[]');
  if (!Array.isArray(parsed)) throw new Error(`${field} musi być tablicą JSON.`);
  return parsed;
}

function cardToForm(card: Any): Any {
  return {
    categorySlug: card.categorySlug || FALLBACK_CATEGORIES[0]!.slug,
    baseSlug: card.baseSlug || '',
    title: card.title || '',
    summary: card.summary || '',
    bodyMd: card.bodyMd || '',
    seoTitle: card.seoTitle || '',
    seoDescription: card.seoDescription || '',
    imageAlt: card.imageAlt || '',
    coverImage: card.coverImage || '',
    facts: JSON.stringify(card.facts ?? [], null, 2),
    specs: JSON.stringify(card.specs ?? [], null, 2),
    faq: JSON.stringify(card.faq ?? [], null, 2),
    quiz: card.quiz ? JSON.stringify(card.quiz, null, 2) : '',
    sources: JSON.stringify(card.sources ?? [], null, 2),
    factStatus: card.factStatus || 'unverified',
    verifiedAt: localInput(card.verifiedAt),
  };
}

/**
 * Encyclopedia & Tech Content Engine — P0.
 *
 * It can promote an approved NieboGuard article into a structured encyclopedia
 * card, edit the verified blocks and send it to NieboGuard's review endpoint.
 * It never publishes directly and never fills missing technical facts.
 */
export function NieboGuardEncyclopediaSection() {
  const [bridge, setBridge] = useState<Any | null>(null);
  const [cards, setCards] = useState<Any[]>([]);
  const [articles, setArticles] = useState<Any[]>([]);
  const [articleId, setArticleId] = useState('');
  const [category, setCategory] = useState(FALLBACK_CATEGORIES[0]!.slug);
  const [selected, setSelected] = useState<Any | null>(null);
  const [form, setForm] = useState<Any | null>(null);
  const [pkg, setPkg] = useState<Any | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const cardPage = usePagination(cards, 12);

  const load = useCallback(async () => {
    setError(null);
    const [nextBridge, nextCards, nextArticles] = await Promise.all([
      api.nieboguard.content.encyclopediaStatus(),
      api.nieboguard.content.encyclopediaCards(200),
      api.nieboguard.content.articles(),
    ]);
    setBridge(nextBridge);
    setCards(nextCards.cards ?? []);
    setArticles(nextArticles.items ?? []);
    if (!articleId && nextArticles.items?.[0]?.id) setArticleId(nextArticles.items[0].id);
  }, [articleId]);

  useEffect(() => { load().catch(setError); }, [load]);

  const act = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key); setError(null); setMessage('');
    try {
      await action();
      setMessage(success);
      await load();
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const open = (card: Any) => {
    setSelected(card);
    setForm(cardToForm(card));
    setPkg(card.packageJson ?? null);
    setMessage('');
    setError(null);
  };

  const set = (key: string, value: unknown) => setForm((current: Any) => current ? { ...current, [key]: value } : current);

  const save = async () => {
    if (!selected || !form) return;
    setBusy('save'); setError(null); setMessage('');
    try {
      const verifiedAt = form.factStatus === 'verified'
        ? (form.verifiedAt ? new Date(form.verifiedAt).toISOString() : new Date().toISOString())
        : null;
      const quiz = String(form.quiz || '').trim() ? JSON.parse(String(form.quiz)) : null;
      const result = await api.nieboguard.content.updateEncyclopediaCard(selected.id, {
        categorySlug: form.categorySlug,
        baseSlug: form.baseSlug,
        title: form.title,
        summary: form.summary,
        bodyMd: form.bodyMd,
        seoTitle: form.seoTitle,
        seoDescription: form.seoDescription,
        imageAlt: form.imageAlt,
        coverImage: String(form.coverImage || '').trim() || null,
        facts: parseArray(form.facts, 'Fakty').map(String),
        specs: parseArray(form.specs, 'Specyfikacja'),
        faq: parseArray(form.faq, 'FAQ'),
        quiz,
        sources: parseArray(form.sources, 'Źródła'),
        factStatus: form.factStatus,
        verifiedAt,
      });
      setSelected(result.card);
      setForm(cardToForm(result.card));
      setMessage('Karta zapisana jako DRAFT. Wysyłka utworzy wpis do przeglądu w NieboGuard.');
      await load();
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const categories = bridge?.categories?.length ? bridge.categories : FALLBACK_CATEGORIES;
  const configured = Boolean(bridge?.configured?.token);
  const counts = bridge?.counts ?? [];
  const count = (name: string) => Number(counts.find((row: Any) => row.status === name)?.n ?? 0);
  const topics = bridge?.topics ?? [];
  const topicPage = usePagination<Any>(topics, 12);
  const categoryLabel = (slug: string) => categories.find((item: Any) => item.slug === slug)?.label ?? slug;
  const canPush = Boolean(selected && selected.factStatus === 'verified' && selected.sources?.length && configured);

  if (!bridge) return error ? <ErrorAlert error={error} /> : <div className="nb-list"><div className="loading">Ładowanie encyklopedii…</div></div>;

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}

    <div className="nb-hero">
      <div className="nb-row nb-row--between">
        <div>
          <h2>Encyclopedia & Tech Content Engine</h2>
          <p>Zatwierdzony artykuł NieboGuard zamieniamy w ustrukturyzowaną kartę techniczną: źródła, fakty, specyfikację, FAQ i quiz. Karta trafia do NieboGuard jako <code>review</code>; nie jest publikowana automatycznie i nie uzupełniamy brakujących parametrów domysłami.</p>
        </div>
        <Badge tone={configured ? 'success' : 'danger'}>{configured ? 'Token mostu ustawiony' : 'Brak tokenu mostu'}</Badge>
      </div>
      <div className="nb-row">
        {categories.map((item: Any) => <Badge key={item.slug} tone="info">{item.label}</Badge>)}
      </div>
    </div>

    <div className="nb-grid nb-grid--stats">
      <StatCard label="Karty" value={cards.length} />
      <StatCard label="Szkice" value={count('DRAFT')} />
      <StatCard label="Wysłane" value={count('PUSHED')} />
      <StatCard label="Opublikowane" value={count('PUBLISHED')} />
      <StatCard label="Tematy" value={topics.length} />
    </div>

    <Card title="Topic Radar — tematy encyklopedii" actions={<span className="nb-stat-note">{topics.length} tematów · metadane, nie publikacje</span>}>
      <p className="nb-stat-note">Tematy pochodzą z katalogu kategorii i nie są automatycznie treścią. „Utwórz kartę” zakłada pusty szkic z tytułem, kategorią i źródłem startowym; redaktor uzupełnia treść i weryfikuje fakty.</p>
      {topics.length === 0 ? <EmptyState title="Brak tematów" hint="Uruchom migrację 0145." /> :
        <div className="nb-scroll" style={{ maxHeight: 420 }}><table className="table">
          <thead><tr><th>Temat</th><th>Kategoria</th><th>Priorytet</th><th>Status</th><th>Źródło</th><th /></tr></thead>
          <tbody>{topicPage.pageRows.map((topic: Any) => <tr key={topic.id}>
            <td><strong>{topic.title}</strong><div className="nb-item__meta"><code>{topic.slug}</code> · {topic.language}</div></td>
            <td>{categoryLabel(topic.categorySlug)}</td>
            <td>{topic.priority}</td>
            <td><Badge tone={topic.status === 'READY' ? 'success' : topic.status === 'NEEDS_REVIEW' ? 'warning' : 'neutral'}>{topic.status}</Badge><div className="nb-item__meta">{topic.approvalStatus}</div></td>
            <td>{topic.sourceUrl ? <a href={topic.sourceUrl} target="_blank" rel="noreferrer">{topic.sourceTitle || 'źródło'} ↗</a> : '—'}</td>
            <td><Button size="sm" disabled={busy !== null} onClick={() => act(`topic-${topic.id}`, async () => {
              const result = await api.nieboguard.content.createEncyclopediaFromTopic(topic.id);
              if (result.card) open(result.card);
            }, 'Szkic karty utworzony z tematu. Uzupełnij treść i źródła.')}>{busy === `topic-${topic.id}` ? 'Tworzę…' : 'Utwórz kartę'}</Button></td>
          </tr>)}</tbody>
        </table><Pagination page={topicPage.page} pageSize={topicPage.pageSize} total={topicPage.total} onChange={topicPage.setPage} /></div>}
    </Card>

    <Card title="Nowa karta z artykułu" actions={<Button size="sm" variant="ghost" onClick={() => load().catch(setError)} disabled={busy !== null}>Odśwież</Button>}>
      <p className="nb-stat-note">Źródłem jest artykuł marki NieboGuard w statusie APPROVED/PUBLISHED. Tytuł, lead, Markdown, FAQ i źródła są kopiowane; fakty i specyfikację uzupełnia redaktor po weryfikacji.</p>
      <div className="nb-row" style={{ alignItems: 'flex-end' }}>
        <label className="field" style={{ flex: 1, minWidth: 260 }}><span>Artykuł</span>
          <select value={articleId} onChange={(event) => setArticleId(event.target.value)}>
            {articles.length === 0 ? <option value="">Brak zatwierdzonych artykułów NieboGuard</option> : null}
            {articles.map((article) => <option key={article.id} value={article.id}>{article.title} · {article.language}</option>)}
          </select>
        </label>
        <label className="field"><span>Kategoria</span>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            {categories.map((item: Any) => <option key={item.slug} value={item.slug}>{item.label}</option>)}
          </select>
        </label>
        <Button
          variant="primary"
          disabled={!configured || !articleId || busy !== null}
          onClick={() => act('create', async () => {
            const result = await api.nieboguard.content.createEncyclopediaFromArticle(articleId, category);
            if (result.card) open(result.card);
          }, 'Karta utworzona jako szkic. Uzupełnij dane i oznacz weryfikację.')}
        >{busy === 'create' ? 'Tworzę…' : 'Utwórz kartę'}</Button>
      </div>
    </Card>

    <Card title="Karty encyklopedii" actions={<span className="nb-stat-note">{cards.length} kart</span>}>
      {cards.length === 0 ? <EmptyState title="Brak kart" hint="Utwórz kartę z zatwierdzonego artykułu." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Tytuł</th><th>Kategoria</th><th>Status</th><th>Weryfikacja</th><th>Źródła</th><th>NieboGuard</th><th /></tr></thead>
          <tbody>{cardPage.pageRows.map((card) => <tr key={card.id}>
            <td><strong>{card.title}</strong><div className="nb-item__meta"><code>{card.baseSlug}</code> · {card.language}</div></td>
            <td>{categories.find((item: Any) => item.slug === card.categorySlug)?.label ?? card.categorySlug}</td>
            <td><Badge tone={statusTone(card.status)}>{card.status}</Badge>{card.lastError ? <div className="nb-item__meta nb-danger">{card.lastError}</div> : null}</td>
            <td><Badge tone={card.factStatus === 'verified' ? 'success' : card.factStatus === 'disputed' ? 'danger' : 'warning'}>{card.factStatus}</Badge><div className="nb-item__meta">{fmt(card.verifiedAt)}</div></td>
            <td>{Array.isArray(card.sources) ? card.sources.length : 0}</td>
            <td>{card.externalUrl ? <a href={card.externalUrl} target="_blank" rel="noreferrer">karta ↗</a> : <span className="nb-muted">niewysłana</span>}</td>
            <td><Button size="sm" onClick={() => open(card)}>Edytuj</Button></td>
          </tr>)}</tbody>
        </table><Pagination page={cardPage.page} pageSize={cardPage.pageSize} total={cardPage.total} onChange={cardPage.setPage} /></div>}
    </Card>

    {selected && form ? <Card
      title={`Edycja: ${selected.title}`}
      actions={<div className="btn-row">
        <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setForm(null); }}>Zamknij</Button>
      </div>}
    >
      <div className="nb-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
        <label className="field"><span>Tytuł</span><input value={form.title} onChange={(event) => set('title', event.target.value)} /></label>
        <label className="field"><span>Slug</span><input value={form.baseSlug} onChange={(event) => set('baseSlug', event.target.value)} /></label>
        <label className="field"><span>Kategoria</span><select value={form.categorySlug} onChange={(event) => set('categorySlug', event.target.value)}>{categories.map((item: Any) => <option key={item.slug} value={item.slug}>{item.label}</option>)}</select></label>
        <label className="field"><span>Status faktów</span><select value={form.factStatus} onChange={(event) => set('factStatus', event.target.value)}><option value="unverified">unverified</option><option value="verified">verified</option><option value="disputed">disputed</option></select></label>
        <label className="field"><span>Data weryfikacji</span><input type="datetime-local" value={form.verifiedAt} onChange={(event) => set('verifiedAt', event.target.value)} /></label>
        <label className="field"><span>Cover URL</span><input value={form.coverImage} onChange={(event) => set('coverImage', event.target.value)} placeholder="https://…" /></label>
      </div>
      <label className="field"><span>Lead</span><textarea rows={3} value={form.summary} onChange={(event) => set('summary', event.target.value)} /></label>
      <label className="field"><span>Treść Markdown</span><textarea rows={10} value={form.bodyMd} onChange={(event) => set('bodyMd', event.target.value)} /></label>
      <div className="nb-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        <label className="field"><span>SEO title</span><input value={form.seoTitle} onChange={(event) => set('seoTitle', event.target.value)} /></label>
        <label className="field"><span>SEO description</span><input value={form.seoDescription} onChange={(event) => set('seoDescription', event.target.value)} /></label>
        <label className="field"><span>Alt obrazu</span><input value={form.imageAlt} onChange={(event) => set('imageAlt', event.target.value)} /></label>
      </div>
      <div className="nb-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <label className="field"><span>Fakty (JSON: string[])</span><textarea rows={7} value={form.facts} onChange={(event) => set('facts', event.target.value)} /></label>
        <label className="field"><span>Specyfikacja (JSON: {`{label,value}`}[])</span><textarea rows={7} value={form.specs} onChange={(event) => set('specs', event.target.value)} /></label>
        <label className="field"><span>FAQ (JSON: {`{q,a}`}[])</span><textarea rows={7} value={form.faq} onChange={(event) => set('faq', event.target.value)} /></label>
        <label className="field"><span>Źródła (JSON)</span><textarea rows={7} value={form.sources} onChange={(event) => set('sources', event.target.value)} /></label>
        <label className="field"><span>Quiz (JSON, opcjonalnie)</span><textarea rows={7} value={form.quiz} onChange={(event) => set('quiz', event.target.value)} /></label>
      </div>
      <div className="nb-row" style={{ marginTop: 12 }}>
        <Button variant="primary" onClick={save} disabled={busy !== null}>{busy === 'save' ? 'Zapisuję…' : 'Zapisz szkic'}</Button>
        <Button variant="success" onClick={() => { setForm((current: Any) => ({ ...current, factStatus: 'verified', verifiedAt: current.verifiedAt || localInput(new Date().toISOString()) })); setMessage('Ustawiono verified — kliknij „Zapisz szkic”, aby utrwalić weryfikację.'); }} disabled={busy !== null}>Oznacz jako zweryfikowane</Button>
        <Button
          onClick={() => act('push', async () => { await api.nieboguard.content.pushEncyclopediaCard(selected.id); const detail = await api.nieboguard.content.encyclopediaCard(selected.id); open(detail.card); }, 'Karta wysłana do NieboGuard jako review.')}
          disabled={busy !== null || !canPush}
        >{busy === 'push' ? 'Wysyłam…' : 'Wyślij do NieboGuard (review)'}</Button>
        <Button
          variant="ghost"
          onClick={() => act('sync', async () => { const result = await api.nieboguard.content.syncEncyclopediaCard(selected.id); if (result.card) open(result.card); }, 'Status karty odświeżony.')}
          disabled={busy !== null || !selected.externalId}
        >{busy === 'sync' ? 'Sprawdzam…' : 'Sprawdź status'}</Button>
        <Button
          variant="ghost"
          onClick={() => act('package', async () => { const result = await api.nieboguard.content.generateEncyclopediaPackage(selected.id); setPkg(result.package); open(result.card); }, 'Pakiet planistyczny wygenerowany. Media nadal wymagają renderu i review.')}
          disabled={busy !== null || !selected.bodyMd}
        >{busy === 'package' ? 'Generuję…' : 'Generuj pakiet treści'}</Button>
        <Button
          variant="primary"
          onClick={() => act('render', async () => { const result = await api.nieboguard.content.renderEncyclopediaPackage(selected.id); setPkg(result.package); open(result.card); }, 'Zlecono render 3 Shortów i Longa do istniejącej kolejki. Media wymagają review.')}
          disabled={busy !== null || selected.factStatus !== 'verified' || !selected.sources?.length}
        >{busy === 'render' ? 'Zlecam…' : 'Zleć render Short + Long'}</Button>
        <Button
          variant="ghost"
          onClick={() => act('render-sync', async () => { const result = await api.nieboguard.content.refreshEncyclopediaRender(selected.id); setPkg(result.package); open(result.card); }, 'Status renderu odświeżony.')}
          disabled={busy !== null || !selected.packageJson?.render}
        >{busy === 'render-sync' ? 'Sprawdzam…' : 'Odśwież render'}</Button>
        <Button
          variant="ghost"
          onClick={() => act('social', async () => { await api.nieboguard.content.createEncyclopediaSocialDraft(selected.id); }, 'Utworzono szkice social w Content Studio (DRAFT). Wymagają redakcji i harmonogramu.')}
          disabled={busy !== null}
        >{busy === 'social' ? 'Tworzę…' : 'Utwórz szkice social'}</Button>
        <Button
          variant="danger"
          onClick={() => { if (!window.confirm('Zarchiwizować kartę i wycofać ją z NieboGuard?')) return; act('archive', async () => { await api.nieboguard.content.archiveEncyclopediaCard(selected.id); setSelected(null); setForm(null); setPkg(null); }, 'Karta zarchiwizowana i wycofana z NieboGuard.'); }}
          disabled={busy !== null || selected.status === 'ARCHIVED'}
        >{busy === 'archive' ? 'Archiwizuję…' : 'Archiwizuj / wycofaj'}</Button>
        <span className="nb-stat-note">Wysyłka wymaga: verified + data + min. 1 źródło. NieboGuard może wymagać osobnej publikacji przez admina.</span>
      </div>
      {pkg ? <div style={{ marginTop: 16 }}>
        <div className="nb-row nb-row--between"><strong>Pakiet treści (plan)</strong><Badge tone="warning">{String(pkg.mediaStatus || 'NOT_RENDERED')}</Badge></div>
        <p className="nb-stat-note">Wygenerowane deterministycznie z zatwierdzonych bloków. To nie są gotowe filmy ani grafiki: brakuje renderu, TTS, licencji mediów i review. Nie publikujemy pakietu automatycznie.</p>
        <div className="nb-grid nb-grid--stats">
          <StatCard label="Shorty" value={Array.isArray(pkg.shorts) ? pkg.shorts.length : 0} />
          <StatCard label="Infografiki" value={Array.isArray(pkg.infographics) ? pkg.infographics.length : 0} />
          <StatCard label="Platformy" value={pkg.social ? Object.keys(pkg.social).length : 0} />
          <StatCard label="Źródła" value={Array.isArray(pkg.sources) ? pkg.sources.length : 0} />
        </div>
        <div className="nb-list">
          <div className="nb-item"><div className="nb-item__title">{pkg.article?.title || 'Artykuł encyklopedyczny'}</div><div className="nb-item__meta">{String(pkg.article?.summary || '').slice(0, 240)}</div></div>
          <div className="nb-item"><div className="nb-item__title">{pkg.educational?.title || 'Artykuł edukacyjny'}</div><div className="nb-item__meta">{String(pkg.educational?.angle || '')}</div></div>
          <div className="nb-item"><div className="nb-item__title">Long: {pkg.long?.title || '—'}</div><div className="nb-item__meta">{Array.isArray(pkg.long?.sections) ? pkg.long.sections.map((section: Any) => section.heading).join(' · ') : ''}</div></div>
          {Array.isArray(pkg.shorts) ? pkg.shorts.map((short: Any, index: number) => <div className="nb-item" key={`short-${index}`}><div className="nb-item__title">Short {short.durationSeconds}s · {short.angle}</div><div className="nb-item__meta">{short.hook} · {String(short.script || '').slice(0, 180)}</div></div>) : null}
          <div className="nb-item"><div className="nb-item__title">Quiz</div><div className="nb-item__meta">{Array.isArray(pkg.quiz?.questions) && pkg.quiz.questions.length ? `${pkg.quiz.questions.length} pytań` : 'Brak zatwierdzonego quizu — uzupełnij ręcznie.'}</div></div>
          <div className="nb-item"><div className="nb-item__title">Render</div><div className="nb-item__meta">{pkg.render ? `${pkg.render.productions?.length || 0} produkcji · ${pkg.render.mediaStatus || 'RENDER_REQUESTED'}` : 'Nie zlecono renderu. Kolejka VPS i review są osobnym etapem.'}</div>
            {Array.isArray(pkg.render?.productions) ? pkg.render.productions.map((production: Any) => <div className="nb-item__meta" key={production.id}>{production.kind} {production.angle} · {production.status} · <code>{production.id}</code></div>) : null}
            {Array.isArray(pkg.render?.errors) && pkg.render.errors.length ? <div className="nb-item__meta nb-danger">Błędy: {pkg.render.errors.map((error: Any) => `${error.kind}/${error.angle}: ${error.message}`).join(' · ')}</div> : null}
          </div>
          <div className="nb-item"><div className="nb-item__title">Social</div><div className="nb-item__meta">{pkg.socialDraft?.contentId ? `Szkice DRAFT: ${pkg.socialDraft.platforms?.join(', ')} · ${pkg.socialDraft.contentId}` : 'Szkice social nie zostały jeszcze utworzone. Nie są publikowane automatycznie.'}</div></div>
        </div>
      </div> : null}
    </Card> : null}
  </div>;
}
